import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation, Link } from 'react-router-dom';
import Header from './components/Header';
import Hero from './components/Hero';
import About from './components/About';
import Portfolio from './components/Portfolio';
import LeadForm from './components/LeadForm';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import ImageLightbox from './components/ImageLightbox';
import ServiceDetail from './pages/ServiceDetail';
import AboutUs from './pages/AboutUs';
import Gallery from './pages/Gallery';
import Contact from './pages/Contact';
import { portfolioImages } from './data';

// Helper component to handle scrolling to hash sections or top of page on route change
function ScrollToHashAndTop() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        // Delay slightly to allow the DOM to render
        const timer = setTimeout(() => {
          const offset = 120; // Header height offset
          const bodyRect = document.body.getBoundingClientRect().top;
          const elementRect = element.getBoundingClientRect().top;
          const elementPosition = elementRect - bodyRect;
          const offsetPosition = elementPosition - offset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }, 150);
        return () => clearTimeout(timer);
      }
    } else {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [location]);

  return null;
}

function App() {
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);

  useEffect(() => {
    // Set default document title and meta description
    document.title = "Peter Sloth | Entreprenør i Brædstrup, Horsens, Silkeborg & Skanderborg";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", "Leder du efter en professionel entreprenør i Horsens, Silkeborg, Skanderborg eller Brædstrup? Peter Sloth udfører terrændæk, sokkel, belægning, støttemur, gravearbejde og nedbrydning.");
    } else {
      const meta = document.createElement('meta');
      meta.name = "description";
      meta.content = "Leder du efter en professionel entreprenør i Horsens, Silkeborg, Skanderborg eller Brædstrup? Peter Sloth udfører terrændæk, sokkel, belægning, støttemur, gravearbejde og nedbrydning.";
      document.head.appendChild(meta);
    }
  }, []);

  const handlePrevImage = () => {
    setSelectedImageIndex((prevIndex) => {
      if (prevIndex === null) return null;
      return prevIndex === 0 ? portfolioImages.length - 1 : prevIndex - 1;
    });
  };

  const handleNextImage = () => {
    setSelectedImageIndex((prevIndex) => {
      if (prevIndex === null) return null;
      return prevIndex === portfolioImages.length - 1 ? 0 : prevIndex + 1;
    });
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased flex flex-col">
      {/* Scroll and Hash Manager */}
      <ScrollToHashAndTop />

      {/* Cookie Consent Overlay */}
      <CookieConsent />

      {/* Header & Navigation */}
      <Header />

      {/* Main Content Area with Routing */}
      <main className="flex-grow">
        <Routes>
          {/* Home Page Route */}
          <Route
            path="/"
            element={
              <>
                {/* Hero Section with Overlapping Category Cards */}
                <Hero />

                {/* About & Core Capabilities Section */}
                <About />

                {/* Portfolio Showcase */}
                <Portfolio onImageClick={setSelectedImageIndex} />

                {/* Split-Screen Lead Generation & Quote Form */}
                <LeadForm />
              </>
            }
          />

          {/* Service Detail Subpage Route */}
          <Route path="/ydelser/:id" element={<ServiceDetail />} />

          {/* About Us Subpage Route */}
          <Route path="/om-os" element={<AboutUs />} />

          {/* Gallery Subpage Route */}
          <Route path="/galleri" element={<Gallery />} />

          {/* Contact Subpage Route */}
          <Route path="/kontakt" element={<Contact />} />

          {/* Fallback Route */}
          <Route
            path="*"
            element={
              <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 text-center pt-28">
                <h1 className="text-4xl font-extrabold text-slate-900 uppercase tracking-tight">Siden blev ikke fundet</h1>
                <p className="mt-4 text-slate-600 max-w-md">Siden du leder efter eksisterer ikke eller er blevet flyttet.</p>
                <Link
                  to="/"
                  className="mt-8 bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase text-sm tracking-wider py-4 px-8 transition-all duration-200 rounded-none shadow-lg"
                >
                  Gå til forsiden
                </Link>
              </div>
            }
          />
        </Routes>
      </main>

      {/* Main Footer */}
      <Footer />

      {/* Image Lightbox */}
      {selectedImageIndex !== null && (
        <ImageLightbox
          imageIndex={selectedImageIndex}
          onClose={() => setSelectedImageIndex(null)}
          onPrev={handlePrevImage}
          onNext={handleNextImage}
        />
      )}
    </div>
  );
}

export default App;
